import { formatTime, parseTime } from "../TimePicker/timeMath";
import type { Lane, MinuteRange, TimeRange } from "./TimeRangePicker.types";

export function toMinutes(value: string | null | undefined): number | null {
  const time = parseTime(value);

  return time ? time.hour * 60 + time.minute : null;
}

export function fromMinutes(minutes: number): string {
  // Rounded whole, not minute by minute: rounding the remainder alone turns
  // 19:59.6 into an hour that never existed.
  const total = Math.round(minutes);

  return formatTime({ hour: Math.floor(total / 60), minute: total % 60 });
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function snap(minutes: number, step: number): number {
  return Math.round(minutes / step) * step;
}

export function toMinuteRange(range: TimeRange): MinuteRange | null {
  const begin = toMinutes(range.begin);
  const end = toMinutes(range.end);

  return null === begin || null === end || end <= begin ? null : { begin, end };
}

export function fromMinuteRange(range: MinuteRange): TimeRange {
  return { begin: fromMinutes(range.begin), end: fromMinutes(range.end) };
}

export function formatRange(range: MinuteRange): string {
  return `${fromMinutes(range.begin)}–${fromMinutes(range.end)}`;
}

export function assignLanes(ranges: (MinuteRange | null)[]): Lane[] {
  const lanes: Lane[] = ranges.map(() => ({ lane: 0, lanes: 1 }));

  const order = ranges
    .map((range, index) => ({ range, index }))
    .filter(
      (entry): entry is { range: MinuteRange; index: number } =>
        null !== entry.range,
    )
    .sort((a, b) => a.range.begin - b.range.begin || a.range.end - b.range.end);

  let laneEnds: number[] = [];
  let cluster: number[] = [];

  const closeCluster = () => {
    for (const index of cluster) {
      lanes[index]!.lanes = laneEnds.length;
    }

    laneEnds = [];
    cluster = [];
  };

  for (const { range, index } of order) {
    if (0 < cluster.length && laneEnds.every((end) => end <= range.begin)) {
      closeCluster();
    }

    let lane = laneEnds.findIndex((end) => end <= range.begin);

    if (-1 === lane) {
      lane = laneEnds.length;
      laneEnds.push(range.end);
    } else {
      laneEnds[lane] = range.end;
    }

    lanes[index]!.lane = lane;
    cluster.push(index);
  }

  closeCluster();

  return lanes;
}

/**
 * The room a range can grow or move into without touching another one: the
 * closest end on its left and the closest begin on its right. `around` stands
 * in for a range that does not exist yet.
 */
export function neighbourBounds(
  ranges: (MinuteRange | null)[],
  index: number | null,
  around: MinuteRange,
  min: number,
  max: number,
): MinuteRange {
  let lower = min;
  let upper = max;

  ranges.forEach((range, candidate) => {
    if (null === range || candidate === index) {
      return;
    }

    if (range.end <= around.begin) {
      lower = Math.max(lower, range.end);
    } else if (around.end <= range.begin) {
      upper = Math.min(upper, range.begin);
    }
  });

  return { begin: lower, end: upper };
}

// Steps a reader expects to see on a clock, coarsest last.
const TICK_LADDER = [5, 15, 30, 60, 120, 180, 360, 720];

/**
 * The finest step from the ladder that keeps a span under `target` ticks,
 * never coarser than the one the caller asked for. Zooming in this way
 * refines the grid on its own.
 */
export function tickStepFor(
  span: number,
  coarsest: number,
  target: number,
): number {
  const step =
    TICK_LADDER.find((candidate) => span / candidate <= target) ?? coarsest;

  return Math.min(step, coarsest);
}

/** A window kept inside the day, never shorter than `minSpan`. */
export function clampView(
  view: MinuteRange,
  min: number,
  max: number,
  minSpan: number,
): MinuteRange {
  // Zooming multiplies the span, so the window is put back on whole minutes
  // before anything reads it.
  const span = clamp(
    Math.round(view.end - view.begin),
    Math.min(minSpan, max - min),
    max - min,
  );
  const begin = clamp(Math.round(view.begin), min, max - span);

  return { begin, end: begin + span };
}

export function ticksBetween(
  min: number,
  max: number,
  every: number,
): number[] {
  const ticks: number[] = [];

  for (let tick = Math.ceil(min / every) * every; tick <= max; tick += every) {
    ticks.push(tick);
  }

  return ticks;
}
